// client/src/services/tokenService.js
import axios from 'axios';

// CORRETTO: Refresh token ogni 1 ora (1 ora prima della scadenza di 2 ore)
const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000; // 1 ora
// Session timeout is 15 minutes
const SESSION_TIMEOUT = 15 * 60 * 1000;

class TokenService {
  constructor() {
    this.refreshTimerId = null;
    this.refreshingPromise = null;
    this.lastActivityTime = Date.now();
  }

  // Update last activity time
  updateActivity() {
    this.lastActivityTime = Date.now();
  }

  // Get last activity time
  getLastActivityTime() {
    return this.lastActivityTime;
  }

  // Check if user has been inactive for too long
  isSessionExpired() {
    const inactiveTime = Date.now() - this.lastActivityTime;
    return inactiveTime >= SESSION_TIMEOUT;
  }

  // Start automatic token refresh
  startTokenRefresh(onRefreshSuccess, onRefreshError) {
    
    // Clear any existing refresh timers
    this.stopTokenRefresh();
    
    // Make sure there's a token to refresh before setting the timer
    const token = localStorage.getItem('token');
    if (!token) {
       return Promise.resolve();
    }
    
    // Set initial activity time
    this.lastActivityTime = Date.now();
    
    // Set a new timer for periodic refresh
    this.refreshTimerId = setInterval(async () => {     
      try {
        // Check if token still exists before attempting refresh
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
          this.stopTokenRefresh();
          return;
        }
        
        // Check if user has been inactive for too long
        if (this.isSessionExpired()) {
          //console.log('User inactive for 15 minutes, stopping token refresh');
          this.stopTokenRefresh();
          if (onRefreshError) {
            onRefreshError(new Error('Session timeout due to inactivity'));
          }
          return;
        }
        
        // Only refresh if the user has been active
        await this.refreshToken(onRefreshSuccess, onRefreshError);
      } catch (error) {
        //console.error('Error in refresh timer:', error);
        // If there's an authentication error, stop the refresh cycle
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.stopTokenRefresh();
          if (onRefreshError) {
            onRefreshError(error);
          }
        }
      }
    }, TOKEN_REFRESH_INTERVAL);
    
    return Promise.resolve();
  }

  // Stop automatic token refresh
  stopTokenRefresh() {
    if (this.refreshTimerId) {
      clearInterval(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  // Perform token refresh
  async refreshToken(onRefreshSuccess, onRefreshError) {
    // If a refresh is already in progress, wait for it to complete
    if (this.refreshingPromise) {
      return this.refreshingPromise;
    }

    // Check if a token exists to refresh
    const token = localStorage.getItem('token');
    if (!token) {
      return Promise.reject(new Error('No token to refresh'));
    }

    // Check if session has expired due to inactivity
    if (this.isSessionExpired()) {
      const error = new Error('Session timeout due to inactivity');
      if (onRefreshError) {
        onRefreshError(error);
      }
      return Promise.reject(error);
    }

    // Create a promise for the refresh
    this.refreshingPromise = new Promise(async (resolve, reject) => {
      try {
        // Make the token refresh request
        const response = await axios.get('/api/auth/refresh-token');
        const newToken = response.data.token;
        
        // Update authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Save the new token to localStorage
        localStorage.setItem('token', newToken);
        
        // Update activity time on successful refresh
        this.updateActivity();
        
        // Notify about successful refresh
        if (onRefreshSuccess) {
          onRefreshSuccess(newToken, response.data.user);
        }
        
        resolve(newToken);
      } catch (error) {
        //console.error('Error refreshing token:', error);
        
        // Handle authorization errors
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          if (onRefreshError) {
            onRefreshError(error);
          }
        }
        
        reject(error);
      } finally {
        // Reset refresh promise
        this.refreshingPromise = null;
      }
    });

    return this.refreshingPromise;
  }

  // Manually refresh token
  manualRefresh() {
    const token = localStorage.getItem('token');
    if (!token) {
      //console.warn('Cannot manually refresh: No token available');
      return Promise.reject(new Error('No token to refresh'));
    }
    
    // Update activity time on manual refresh
    this.updateActivity();
    return this.refreshToken();
  }

  // Get debug information about the token service
  getDebugInfo() {
    const token = localStorage.getItem('token');
    let tokenInfo = null;
    
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        tokenInfo = {
          issuedAt: new Date(payload.iat * 1000).toLocaleString(),
          expiresAt: new Date(payload.exp * 1000).toLocaleString(),
          timeUntilExpiry: Math.floor((payload.exp * 1000 - Date.now()) / 1000 / 60) + ' minutes'
        };
      } catch (e) {
        tokenInfo = { error: 'Invalid token format' };
      }
    }
    
    return {
      // Timer information
      refreshIntervalMinutes: TOKEN_REFRESH_INTERVAL / 1000 / 60,
      sessionTimeoutMinutes: SESSION_TIMEOUT / 1000 / 60,
      hasActiveTimer: !!this.refreshTimerId,
      isRefreshing: !!this.refreshingPromise,
      
      // Activity information
      lastActivity: new Date(this.lastActivityTime).toLocaleString(),
      isSessionExpired: this.isSessionExpired(),
      
      // Token information
      hasToken: !!token,
      tokenInfo: tokenInfo,
      
      // Next refresh time (approximate)
      nextRefreshAt: this.refreshTimerId ? 
        new Date(Date.now() + TOKEN_REFRESH_INTERVAL).toLocaleString() : 
        'No timer active'
    };
  }

  // Clean up when logging out
  cleanup() {
    this.stopTokenRefresh();
    this.lastActivityTime = Date.now();
  }
}

// Create a singleton instance
const tokenServiceInstance = new TokenService();

// Per debug - SOLO in development
if (process.env.NODE_ENV === 'development') {
  window.tokenService = tokenServiceInstance;
}

// Export the instance
export default tokenServiceInstance;